import { gql } from '@urql/core';
import { produce } from 'immer';

import { globalFeatureKeys, siteFeatureKeys } from '/src/features/_global/queries';
import { optionsFeatureKeys } from './queries';
import { teamAndSharingFeatureKeys } from '/src/features/team-and-sharing/queries';
import { defaultMutationFuction } from '../../graphql/defaultMutationFuction';
import { requestUserDataRefresh } from '/src/services/user-data-refresh';

export const deleteSiteAndAssociatedDataMutation = ({ client, queryClient }) => ({
  mutationFn: ({ siteId }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation DeleteSiteAndAssociatedData($siteId: Int!) {
          deleteSiteAndAssociatedData(siteId: $siteId) {
            id
            name
          }
        }
      `,
      {
        siteId,
      },
    ),
  onSuccess: () => {
    void requestUserDataRefresh().catch(() => {});
    queryClient.invalidateQueries({ ...globalFeatureKeys.capabilities });
    queryClient.invalidateQueries({ queryKey: teamAndSharingFeatureKeys.accountUsers._def });
    return queryClient.invalidateQueries({
      queryKey: ['sites'],
    });
  },
});

export const addSiteMutation = ({ client, queryClient }) => ({
  mutationFn: ({ url }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AddSite($url: String!) {
          addSite(url: $url) {
            id
            name
          }
        }
      `,
      {
        url,
      },
    ),
  onSuccess: () => {
    void requestUserDataRefresh().catch(() => {});
    queryClient.invalidateQueries({ queryKey: teamAndSharingFeatureKeys.accountUsers._def });
    return queryClient.invalidateQueries({
      queryKey: ['sites'],
    });
  },
});

export const setRecordingSettings = ({ client, queryClient }) => ({
  mutationFn: ({
    siteId,
    blockedUrlMatchingRules,
    endSessionOnBlockedUrl,
    maskElements,
    pageTargetingEnabled,
    samplingType,
    samplingValue,
    urlMatchingRules,
  }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation SetRecordingSettings(
          $siteId: Int!
          $blockedUrlMatchingRules: String
          $endSessionOnBlockedUrl: Boolean
          $maskElements: String
          $pageTargetingEnabled: Boolean
          $samplingType: SamplingType
          $samplingValue: Float
          $urlMatchingRules: String
        ) {
          setRecordingSettings(
            siteId: $siteId
            blockedUrlMatchingRules: $blockedUrlMatchingRules
            endSessionOnBlockedUrl: $endSessionOnBlockedUrl
            maskElements: $maskElements
            pageTargetingEnabled: $pageTargetingEnabled
            samplingType: $samplingType
            samplingValue: $samplingValue
            urlMatchingRules: $urlMatchingRules
          ) {
            id
            blockedUrlMatchingRules
            endSessionOnBlockedUrl
            maskElements
            pageTargetingEnabled
            samplingType
            samplingValue
            urlMatchingRules
          }
        }
      `,
      {
        siteId,
        blockedUrlMatchingRules,
        endSessionOnBlockedUrl,
        maskElements,
        pageTargetingEnabled,
        samplingType,
        samplingValue,
        urlMatchingRules,
      },
    ),
  onSuccess: () => {
    return queryClient.invalidateQueries({
      queryKey: ['sites'],
    });
  },
});

export const setSiteRecording = ({ client, queryClient }) => ({
  mutationFn: ({ enable, siteId }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation setSiteRecording($enable: Boolean!, $siteId: Int!) {
          setSiteRecording(enable: $enable, siteId: $siteId) {
            sessionRecording
          }
        }
      `,
      {
        enable,
        siteId,
      },
    ),
  onMutate: async ({ enable, siteId }) => {
    await queryClient.cancelQueries({ ...siteFeatureKeys.site });
    const prevState = queryClient.getQueryData(siteFeatureKeys.site({ id: siteId }))?.site?.sessionRecording ?? false;

    queryClient.setQueryData(siteFeatureKeys.site({ id: siteId }), (old) =>
      old
        ? produce(old, (draft) => {
            if (enable != null) {
              draft.site.sessionRecording = enable;
            }
          })
        : null,
    );

    return { prevState };
  },
  onError: (_, variables, context) => {
    queryClient.setQueryData(siteFeatureKeys.site({ id: variables.siteId }), (old) =>
      old
        ? produce(old, (draft) => {
            draft.site.sessionRecording = context.prevState;
          })
        : null,
    );
  },
  onSettled: async (_, __, { siteId }) => {
    await queryClient.invalidateQueries({
      queryKey: siteFeatureKeys.site({ id: siteId }).queryKey,
    });

    return queryClient.invalidateQueries({
      queryKey: ['sites'],
    });
  },
});

export const updateBlockedIpsMutation = ({ client, queryClient }) => ({
  mutationFn: ({ ipList }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation UpdateBlockedIps($ipList: String!) {
          updateBlockedIps(ipList: $ipList) {
            text
            errors {
              line
              message
            }
          }
        }
      `,
      {
        ipList,
      },
    ),
  onSuccess: (data, variables) => {
    const blockedIps = data?.updateBlockedIps?.text ?? variables.ipList;

    queryClient.setQueryData(optionsFeatureKeys.options.queryKey, (old) =>
      old
        ? {
            ...old,
            options: {
              ...old.options,
              userBlockedIps: blockedIps,
            },
          }
        : old,
    );
  },
});

export const updateUserSettingsMutation = ({ client, queryClient }) => ({
  mutationFn: ({ autoIdentity, dnt, defaultCurrency, explicitAgent, useSecureCookies }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation UpdateUserSettings(
          $autoIdentity: Boolean
          $dnt: Boolean
          $defaultCurrency: String
          $explicitAgent: Boolean
          $useSecureCookies: Boolean
        ) {
          updateUserSettings(
            autoIdentity: $autoIdentity
            dnt: $dnt
            defaultCurrency: $defaultCurrency
            explicitAgent: $explicitAgent
            useSecureCookies: $useSecureCookies
          ) {
            settings {
              autoIdentity
              dnt
              defaultCurrency
              explicitAgent
              useSecureCookies
            }
          }
        }
      `,
      {
        autoIdentity,
        dnt,
        defaultCurrency,
        explicitAgent,
        useSecureCookies,
      },
    ),
  onMutate: async ({ autoIdentity, dnt, defaultCurrency, explicitAgent, useSecureCookies }) => {
    await queryClient.cancelQueries({ ...globalFeatureKeys.me });
    const prevSettings = queryClient.getQueryData(globalFeatureKeys.me.queryKey).me.settings;

    queryClient.setQueryData(globalFeatureKeys.me.queryKey, (old) =>
      old
        ? produce(old, (draft) => {
            if (dnt != null) {
              draft.me.settings.dnt = dnt;
            }
            if (autoIdentity != null) {
              draft.me.settings.autoIdentity = autoIdentity;
            }
            if (defaultCurrency != null) {
              draft.me.settings.defaultCurrency = defaultCurrency;
            }
            if (explicitAgent != null) {
              draft.me.settings.explicitAgent = explicitAgent;
            }
            if (useSecureCookies != null) {
              draft.me.settings.useSecureCookies = useSecureCookies;
            }
          })
        : null,
    );

    return { prevSettings };
  },
  onError: (_, __, context) => {
    queryClient.setQueryData(globalFeatureKeys.me.queryKey, (old) =>
      old && context.prevSettings
        ? produce(old, (draft) => {
            draft.me.settings.dnt = context.prevSettings.dnt;
            draft.me.settings.autoIdentity = context.prevSettings.autoIdentity;
            draft.me.settings.defaultCurrency = context.prevSettings.defaultCurrency;
            draft.me.settings.explicitAgent = context.prevSettings.explicitAgent;
            draft.me.settings.useSecureCookies = context.prevSettings.useSecureCookies;
          })
        : null,
    );
  },
  onSettled: async () => {
    try {
      await requestUserDataRefresh();
    } catch {
      // ignore; cache invalidation below ensures fresh server state is reloaded
    }

    queryClient.invalidateQueries({ ...globalFeatureKeys.me });
  },
});

export const updateSiteSettingsMutation = ({ client, queryClient }) => ({
  mutationFn: ({ siteId, autoIdentify, dnt, defaultCurrency, explicitAgent, useSecureCookies }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation UpdateSiteSettings(
          $siteId: Int!
          $autoIdentify: Boolean
          $dnt: Boolean
          $defaultCurrency: String
          $explicitAgent: Boolean
          $useSecureCookies: Boolean
        ) {
          updateSiteSettings(
            siteId: $siteId
            autoIdentify: $autoIdentify
            dnt: $dnt
            defaultCurrency: $defaultCurrency
            explicitAgent: $explicitAgent
            useSecureCookies: $useSecureCookies
          ) {
            settings {
              autoIdentify
              dnt
              defaultCurrency
              explicitAgent
              useSecureCookies
            }
          }
        }
      `,
      { siteId, autoIdentify, dnt, defaultCurrency, explicitAgent, useSecureCookies },
    ),
  onMutate: async ({ siteId, autoIdentify, dnt, defaultCurrency, explicitAgent, useSecureCookies }) => {
    await queryClient.cancelQueries({ queryKey: ['sites'] });

    const previousSiteQueries = queryClient.getQueriesData({ queryKey: ['sites'] });

    queryClient.setQueriesData({ queryKey: ['sites'] }, (old) =>
      old
        ? produce(old, (draft) => {
            const updateSiteSettings = (site) => {
              if (!site || site.id !== siteId || !site.settings) return;

              if (dnt != null) {
                site.settings.dnt = dnt;
              }
              if (autoIdentify != null) {
                site.settings.autoIdentify = autoIdentify;
              }
              if (defaultCurrency != null) {
                site.settings.defaultCurrency = defaultCurrency;
              }
              if (explicitAgent != null) {
                site.settings.explicitAgent = explicitAgent;
              }
              if (useSecureCookies != null) {
                site.settings.useSecureCookies = useSecureCookies;
              }
            };

            if (Array.isArray(draft.sites)) {
              draft.sites.forEach(updateSiteSettings);
            }

            if (draft.site) {
              updateSiteSettings(draft.site);
            }
          })
        : null,
    );

    return { previousSiteQueries };
  },
  onError: (_error, _variables, context) => {
    context?.previousSiteQueries?.forEach(([queryKey, data]) => {
      queryClient.setQueryData(queryKey, data);
    });
  },

  onSuccess: (data, variables) => {
    const nextSettings = data?.updateSiteSettings?.settings;

    if (!nextSettings) return;

    queryClient.setQueriesData({ queryKey: ['sites'] }, (old) =>
      old
        ? produce(old, (draft) => {
            const applySiteSettings = (site) => {
              if (!site || site.id !== variables.siteId || !site.settings) return;

              site.settings.dnt = nextSettings.dnt;
              site.settings.autoIdentify = nextSettings.autoIdentify;
              site.settings.defaultCurrency = nextSettings.defaultCurrency;
              site.settings.explicitAgent = nextSettings.explicitAgent;
              site.settings.useSecureCookies = nextSettings.useSecureCookies;
            };

            if (Array.isArray(draft.sites)) {
              draft.sites.forEach(applySiteSettings);
            }

            if (draft.site) {
              applySiteSettings(draft.site);
            }
          })
        : old,
    );
  },
});

export const updateSitePublicTrackingApiMutation = ({ client, queryClient }) => ({
  mutationFn: ({ enabled, siteId }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation UpdateSitePublicTrackingApi($enabled: Boolean!, $siteId: Int!) {
          updateSitePublicTrackingApi(enabled: $enabled, siteId: $siteId) {
            id
            publicTrackingApi
            apiKey
          }
        }
      `,
      { enabled, siteId },
    ),

  onSuccess: (_data, variables) => {
    queryClient.invalidateQueries({ queryKey: ['sites'] });
    queryClient.invalidateQueries({ queryKey: siteFeatureKeys.site({ id: variables.siteId }).queryKey });
  },
});

export const regenerateSiteApiKeyMutation = ({ client, queryClient }) => ({
  mutationFn: ({ siteId }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation RegenerateSiteApiKey($siteId: Int!) {
          regenerateSiteApiKey(siteId: $siteId) {
            id
            apiKey
            publicTrackingApi
          }
        }
      `,
      { siteId },
    ),

  onSuccess: (_data, variables) => {
    queryClient.invalidateQueries({ queryKey: ['sites'] });
    queryClient.invalidateQueries({ queryKey: siteFeatureKeys.site({ id: variables.siteId }).queryKey });
  },
});

export const regenerateUserApiCredentialsMutation = ({ client, queryClient }) => ({
  mutationFn: () =>
    defaultMutationFuction(
      client,
      gql`
        mutation RegenerateUserApiCredentials {
          regenerateUserApiCredentials {
            key
            secret
            signature
          }
        }
      `,
      {},
    ),

  onSuccess: () => {
    // refresh apiQuery()
    return queryClient.invalidateQueries({ ...optionsFeatureKeys.api });
  },
});
